import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, FileText, CheckCircle, Package, Users, MapPin, DollarSign, Percent, Instagram } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [currentDrop, setCurrentDrop] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    interest: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    const fetchCurrentDrop = async () => {
      try {
        const response = await axios.get(`${API}/api/drops`);
        const emitaDrop = response.data.find(d => d.id === 'emita' || d.brand?.toLowerCase().includes('emita'));
        if (emitaDrop && emitaDrop.status === 'open') {
          setCurrentDrop(emitaDrop);
        }
      } catch (error) {
        console.error('Failed to fetch drops', error);
      }
    };
    fetchCurrentDrop();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert('Please agree to the Terms and Conditions and Privacy Policy');
      return;
    }
    setFormSubmitting(true);
    
    try {
      await axios.post(`${API}/api/interest`, formData);
      setFormSubmitted(true);
      setFormData({ name: '', whatsapp: '', interest: '' });
      setAgreedToTerms(false);
    } catch (error) {
      setFormSubmitted(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  const processSteps = [
    {
      step: '01',
      title: 'Register interest',
      description: 'No commitment. You signal what you want and we match you with others buying from the same brand.'
    },
    {
      step: '02',
      title: 'Submit item details',
      description: 'Send the exact product, sizing, and configuration as shown on the brand website so the order is accurate.'
    },
    {
      step: '03',
      title: 'Confirmation and deposit',
      description: 'Once the drop is viable, we confirm details and collect a deposit to secure your spot.'
    },
    {
      step: '04',
      title: 'Order placed',
      description: 'We place the shared order directly with the brand once everything is locked in.'
    },
    {
      step: '05',
      title: 'Shipping updates',
      description: 'We post clear updates as the shared shipment moves, so you always know what stage it is in.'
    },
    {
      step: '06',
      title: 'Collection',
      description: 'Your items arrive locally and are ready for pickup at the agreed collection location.'
    },
    {
      step: '07',
      title: 'Final payment',
      description: 'Settle the remaining balance once the item is ready for collection or upon pickup, depending on the drop.'
    }
  ];

  const dropCalendar = [
    { quarter: 'Q1 2026', brand: 'Emita Polo', status: 'completed' },
    { quarter: 'Q2 2026', brand: 'To be announced', status: 'upcoming' },
    { quarter: 'Q3 2026', brand: 'To be announced', status: 'upcoming' },
    { quarter: 'Q4 2026', brand: 'To be announced', status: 'upcoming' }
  ];

  return (
    <div className="min-h-screen bg-[#F2EEE7]" data-testid="home-page">
      {/* Navigation */}
      <nav className="border-b border-[#D4CFC7] bg-[#F2EEE7]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl text-[#5C4033]" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
            <span className="font-bold">SHARED</span><span className="font-normal">equal</span>
          </Link>
          <div className="flex items-center gap-6">
            <button 
              onClick={() => scrollToSection('current-release')}
              className="text-sm font-medium text-[#5C4033] hover:text-[#7A1E2C] transition-colors"
            >
              Current Drop
            </button>
            <button 
              onClick={() => scrollToSection('calendar')}
              className="text-sm font-medium text-[#5C4033] hover:text-[#7A1E2C] transition-colors"
            >
              Calendar
            </button>
            <Link 
              to="/faq"
              className="text-sm font-medium text-[#5C4033] hover:text-[#7A1E2C] transition-colors"
            >
              FAQ
            </Link>
            <Link 
              to="/terms-and-conditions"
              className="text-sm font-medium text-[#5C4033] hover:text-[#7A1E2C] transition-colors"
            >
              Terms
            </Link>
            <Link 
              to="/contact"
              className="text-sm font-medium text-[#5C4033] hover:text-[#7A1E2C] transition-colors"
            >
              Contact
            </Link>
          </div>
        </div>
      </nav>

      {/* Section 1 - Hero */}
      <section className="py-24 lg:py-32 px-6" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-serif font-bold text-[#5C4033] tracking-tight leading-tight">
                Cut shipping costs on premium polo gear.
              </h1>
              <p className="text-lg text-[#6B5B4F] mt-6 leading-relaxed">
                Sharedequal helps polo players save on shipping by coordinating trusted group orders from selected international brands.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <Button 
                  onClick={() => scrollToSection('current-release')}
                  className="bg-[#7A1E2C] hover:bg-[#5C1622] text-white px-8 py-6 text-base font-medium"
                  data-testid="hero-view-release"
                >
                  View Current Drop
                </Button>
                <Button 
                  onClick={() => scrollToSection('interest-form')}
                  variant="outline"
                  className="border-[#7A1E2C] text-[#7A1E2C] hover:bg-[#7A1E2C]/10 px-8 py-6 text-base font-medium"
                  data-testid="hero-join-order"
                >
                  Join Order
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1516756953057-acad3b3d8d2d?w=1260&h=750&fit=crop" 
                alt="Polo players in action"
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 - How It Works (7 Steps) */}
      <section className="py-20 px-6 bg-white" data-testid="process-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif font-bold text-[#5C4033]">How It Works</h2>
            <p className="text-[#6B5B4F] mt-3">Simple, transparent, and cost-effective.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.slice(0, 4).map((item) => (
              <Card key={item.step} className="bg-[#F2EEE7] border-none shadow-none">
                <CardContent className="p-6">
                  <span className="text-3xl font-serif font-bold text-[#7A1E2C]/30">{item.step}</span>
                  <h3 className="text-lg font-serif font-semibold text-[#5C4033] mt-3">{item.title}</h3>
                  <p className="text-[#6B5B4F] mt-2 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {processSteps.slice(4).map((item) => (
              <Card key={item.step} className="bg-[#F2EEE7] border-none shadow-none">
                <CardContent className="p-6">
                  <span className="text-3xl font-serif font-bold text-[#7A1E2C]/30">{item.step}</span>
                  <h3 className="text-lg font-serif font-semibold text-[#5C4033] mt-3">{item.title}</h3>
                  <p className="text-[#6B5B4F] mt-2 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 - Current Drop Status */}
      <section id="current-release" className="py-20 px-6" data-testid="current-release-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif font-bold text-[#5C4033]">Current Drop</h2>
          </div>

          <Card className="bg-white border-[#D4CFC7] overflow-hidden">
            <CardContent className="p-8 lg:p-12">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <Badge variant="outline" className="border-[#6B5B4F] text-[#6B5B4F] bg-[#6B5B4F]/10">Closed</Badge>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-serif font-bold text-[#5C4033]">Emita Polo</h3>
                  <p className="text-[#6B5B4F] mt-2">Premium polo equipment from the UK</p>
                  <p className="text-sm text-[#6B5B4F] mt-4">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Q1 2026 — Drop closed
                  </p>
                  <div className="mt-6 p-4 bg-[#F2EEE7] rounded-lg">
                    <p className="text-sm text-[#5C4033]">
                      This drop has closed and is no longer accepting orders. Follow us on Instagram to be notified of the next drop.
                    </p>
                  </div>
                </div>
                <div className="lg:text-right">
                  <a 
                    href="https://www.instagram.com/sharedequal"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#7A1E2C] hover:bg-[#5C1622] text-white px-8 py-4 rounded-md text-base font-medium transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                    Follow for Updates
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 4 - Interest Form for Future Drops */}
      <section id="interest-form" className="py-20 px-6 bg-white" data-testid="interest-form-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-[#5C4033]">Interested in the next drop?</h2>
            <p className="text-[#6B5B4F] mt-3">Register your interest and we'll notify you when the next drop opens.</p>
          </div>

          {formSubmitted ? (
            <Card className="bg-[#F2EEE7] border-[#D4CFC7]">
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-[#7A1E2C] mx-auto mb-4" />
                <h3 className="text-xl font-serif font-semibold text-[#5C4033]">Thank you for your interest!</h3>
                <p className="text-[#6B5B4F] mt-3">Our team will reach out to confirm exact specifications before the drop deadline.</p>
                <Button 
                  onClick={() => setFormSubmitted(false)}
                  variant="outline"
                  className="mt-6 border-[#7A1E2C] text-[#7A1E2C]"
                >
                  Submit Another
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#F2EEE7] border-[#D4CFC7]">
              <CardContent className="p-8">
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">Name</label>
                    <Input 
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="bg-white border-[#D4CFC7] focus:border-[#7A1E2C] focus:ring-[#7A1E2C]"
                      placeholder="Your name"
                      data-testid="interest-form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">WhatsApp Number</label>
                    <Input 
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                      required
                      className="bg-white border-[#D4CFC7] focus:border-[#7A1E2C] focus:ring-[#7A1E2C]"
                      placeholder="+65 XXXX XXXX"
                      data-testid="interest-form-whatsapp"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">What are you interested in?</label>
                    <Textarea 
                      value={formData.interest}
                      onChange={(e) => setFormData({...formData, interest: e.target.value})}
                      required
                      className="bg-white border-[#D4CFC7] focus:border-[#7A1E2C] focus:ring-[#7A1E2C] min-h-[120px]"
                      placeholder="E.g., Mallets, jerseys, boots, etc."
                      data-testid="interest-form-interest"
                    />
                  </div>
                  
                  <p className="text-sm text-[#6B5B4F] italic">
                    Our team will reach out to confirm exact specifications before the drop deadline.
                  </p>

                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="terms-checkbox"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked)}
                      className="mt-1 border-[#7A1E2C] data-[state=checked]:bg-[#7A1E2C]"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms-checkbox" className="text-sm text-[#5C4033]">
                      I agree to the{' '}
                      <Link to="/terms-and-conditions" className="text-[#7A1E2C] underline hover:no-underline">
                        Terms and Conditions
                      </Link>
                      {' '}and{' '}
                      <Link to="/privacy-policy" className="text-[#7A1E2C] underline hover:no-underline">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>

                  <Button 
                    type="submit"
                    disabled={formSubmitting || !agreedToTerms}
                    className="w-full bg-[#7A1E2C] hover:bg-[#5C1622] text-white py-6 text-base font-medium disabled:opacity-50"
                    data-testid="interest-form-submit"
                  >
                    {formSubmitting ? 'Submitting...' : 'Submit Interest'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Section 5 - Past Drop History */}
      <section className="py-20 px-6" data-testid="history-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif font-bold text-[#5C4033]">Past Drop History</h2>
            <p className="text-[#6B5B4F] mt-3">Proof of concept from our completed group orders.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Emita Polo - Q1 2026 */}
            <Card className="bg-white border-[#D4CFC7]">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="outline" className="border-[#2e7d32] text-[#2e7d32] bg-[#2e7d32]/10">Completed</Badge>
                </div>
                <h3 className="text-2xl font-serif font-bold text-[#5C4033]">Emita Polo</h3>
                <p className="text-sm text-[#6B5B4F] mt-2">Q1 2026</p>
                
                <div className="mt-6 pt-6 border-t border-[#D4CFC7] space-y-3">
                  <div className="flex items-center gap-3 text-[#6B5B4F]">
                    <MapPin className="w-4 h-4 text-[#7A1E2C]" />
                    <span>Singapore</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#6B5B4F]">
                    <Users className="w-4 h-4 text-[#7A1E2C]" />
                    <span>12 participants</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#6B5B4F]">
                    <Package className="w-4 h-4 text-[#7A1E2C]" />
                    <span>26 items ordered</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-[#D4CFC7]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#F2EEE7] p-4 rounded-lg text-center">
                      <DollarSign className="w-5 h-5 text-[#7A1E2C] mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#5C4033]">$2,235</p>
                      <p className="text-xs text-[#6B5B4F]">Total shipping saved</p>
                    </div>
                    <div className="bg-[#F2EEE7] p-4 rounded-lg text-center">
                      <Percent className="w-5 h-5 text-[#7A1E2C] mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#5C4033]">84%</p>
                      <p className="text-xs text-[#6B5B4F]">Average saved</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Casablanca Polo - Q1 2026 */}
            <Card className="bg-white border-[#D4CFC7]">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="outline" className="border-[#2e7d32] text-[#2e7d32] bg-[#2e7d32]/10">Completed</Badge>
                </div>
                <h3 className="text-2xl font-serif font-bold text-[#5C4033]">Casablanca Polo</h3>
                <p className="text-sm text-[#6B5B4F] mt-2">Q1 2026</p>
                
                <div className="mt-6 pt-6 border-t border-[#D4CFC7] space-y-3">
                  <div className="flex items-center gap-3 text-[#6B5B4F]">
                    <MapPin className="w-4 h-4 text-[#7A1E2C]" />
                    <span>Singapore</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#6B5B4F]">
                    <Users className="w-4 h-4 text-[#7A1E2C]" />
                    <span>4 players</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#6B5B4F]">
                    <Package className="w-4 h-4 text-[#7A1E2C]" />
                    <span>17 items ordered</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-[#D4CFC7]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#F2EEE7] p-4 rounded-lg text-center">
                      <DollarSign className="w-5 h-5 text-[#7A1E2C] mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#5C4033]">$190.78</p>
                      <p className="text-xs text-[#6B5B4F]">Total shipping saved</p>
                    </div>
                    <div className="bg-[#F2EEE7] p-4 rounded-lg text-center">
                      <Percent className="w-5 h-5 text-[#7A1E2C] mx-auto mb-1" />
                      <p className="text-xl font-bold text-[#5C4033]">41%</p>
                      <p className="text-xs text-[#6B5B4F]">Average saved</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 6 - Calendar */}
      <section id="calendar" className="py-20 px-6 bg-white" data-testid="calendar-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif font-bold text-[#5C4033]">Drop Calendar</h2>
          </div>

          <div className="space-y-4">
            {dropCalendar.map((item, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-6 rounded-lg ${
                  item.status === 'current' 
                    ? 'bg-[#7A1E2C]/10 border border-[#7A1E2C]/30' 
                    : item.status === 'completed'
                    ? 'bg-[#2e7d32]/10 border border-[#2e7d32]/30'
                    : 'bg-[#F2EEE7]'
                }`}
              >
                <div className="flex items-center gap-4">
                  <Calendar className={`w-5 h-5 ${item.status === 'current' ? 'text-[#7A1E2C]' : item.status === 'completed' ? 'text-[#2e7d32]' : 'text-[#6B5B4F]'}`} />
                  <span className="font-medium text-[#5C4033]">{item.quarter}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`${item.status === 'current' ? 'text-[#7A1E2C] font-medium' : item.status === 'completed' ? 'text-[#2e7d32] font-medium' : 'text-[#6B5B4F]'}`}>
                    {item.brand}
                  </span>
                  {item.status === 'current' && (
                    <Badge className="bg-[#7A1E2C] text-white">Current</Badge>
                  )}
                  {item.status === 'completed' && (
                    <Badge variant="outline" className="border-[#2e7d32] text-[#2e7d32]">Completed</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 - Payment & Terms */}
      <section id="terms" className="py-20 px-6" data-testid="terms-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif font-bold text-[#5C4033]">Payment & Terms</h2>
          </div>

          <Card className="bg-white border-[#D4CFC7]">
            <CardContent className="p-8 lg:p-12">
              <ul className="space-y-4 text-[#6B5B4F]">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#7A1E2C] mt-0.5 flex-shrink-0" />
                  <span>50% deposit upon order confirmation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#7A1E2C] mt-0.5 flex-shrink-0" />
                  <span>Remaining balance upon collection</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#7A1E2C] mt-0.5 flex-shrink-0" />
                  <span>Payment via PayNow or CardUp</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#7A1E2C] mt-0.5 flex-shrink-0" />
                  <span>Orders proceed only when minimum participation is reached</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#7A1E2C] mt-0.5 flex-shrink-0" />
                  <span>Shipping costs split fairly based on order value</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#7A1E2C] mt-0.5 flex-shrink-0" />
                  <span>Customs duties and GST allocated proportionally</span>
                </li>
              </ul>
              <div className="mt-8 pt-6 border-t border-[#D4CFC7] flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/terms-and-conditions" 
                  className="text-[#7A1E2C] hover:underline inline-flex items-center gap-2 font-medium"
                >
                  <FileText className="w-4 h-4" />
                  Terms and Conditions
                </Link>
                <Link 
                  to="/privacy-policy" 
                  className="text-[#7A1E2C] hover:underline inline-flex items-center gap-2 font-medium"
                >
                  <FileText className="w-4 h-4" />
                  Privacy Policy
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 8 - Footer CTA */}
      <section className="py-20 px-6 bg-[#5C4033]" data-testid="footer-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif font-bold text-white">Don't miss the next drop.</h2>
          <p className="text-[#D4CFC7] mt-4">Register your interest and we'll notify you when the next drop opens.</p>
          <div className="flex justify-center mt-8">
            <Button 
              onClick={() => scrollToSection('interest-form')}
              className="bg-white text-[#5C4033] hover:bg-[#F2EEE7] px-8 py-6 text-base font-medium"
            >
              Register Interest
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#5C4033] border-t border-[#6B5B4F]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-[#D4CFC7] text-sm">&copy; 2026 Sharedequal. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/terms-and-conditions" className="text-[#D4CFC7] text-sm hover:text-white transition-colors">
                Terms
              </Link>
              <Link to="/privacy-policy" className="text-[#D4CFC7] text-sm hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/contact" className="text-[#D4CFC7] text-sm hover:text-white transition-colors">
                Contact
              </Link>
              <a 
                href="https://www.instagram.com/sharedequal/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#D4CFC7] hover:text-white transition-colors flex items-center gap-1"
              >
                <Instagram className="w-4 h-4" />
                <span className="text-sm">@sharedequal</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
